import type { McapCanonicalRepresentation } from '@/features/assets/types/asset';
import type { DeliveryFilterOperator } from '@/features/delivery/types/contracts';
import {
  createLogicalSegment,
  createSegmentDelivery,
  deleteLogicalSegment,
  fetchDeliveryDownloadLease,
  fetchLogicalSegment,
  fetchLogicalSegments,
  fetchSegmentDeliveries,
  fetchSegmentDelivery,
  previewSegment,
  searchMcapSegments,
} from '@/features/mcap/api/segments';
import { ACTIVE_STATUS_POLL_MS, MCAP_QUERY_KEY } from '@/features/mcap/config/constants';
import {
  type SegmentPreviewContext,
  type VideoTrackCatalogEntry,
  planMatchesPreviewContext,
  planMatchesVideoTrackCatalog,
} from '@/features/mcap/lib/segmentPlan';
import { SegmentPreviewMismatch } from '@/features/mcap/lib/segmentSubmitError';
import type {
  CreateDeliveryInput,
  DeliveryResult,
  DeliveryTask,
  LogicalSegment,
  SegmentPreviewInput,
} from '@/features/mcap/types/contracts';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

function isDeliveryTerminal(
  delivery: Pick<DeliveryTask, 'status' | 'reconciled'> | undefined,
): boolean {
  return (
    !!delivery &&
    (delivery.status === 'failed' ||
      delivery.status === 'cancelled' ||
      (delivery.status === 'done' && delivery.reconciled))
  );
}

/** Delete a saved logical segment and refresh the asset's segment list. */
export function useDeleteLogicalSegment(assetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (segmentId: string) => deleteLogicalSegment(assetId, segmentId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [MCAP_QUERY_KEY, 'segments', assetId] }),
  });
}

/**
 * Shared options for an asset's logical-segments query, so the route loader can prefetch the
 * exact same query the workspace reads (mirrors `assetQuery` / `mcapMetadataQuery`). The listing
 * is un-paginated — the endpoint returns the asset's full segment list in one response.
 */
export function logicalSegmentsQueryOptions(assetId: string) {
  return queryOptions({
    queryKey: [MCAP_QUERY_KEY, 'segments', assetId],
    queryFn: ({ signal }) => fetchLogicalSegments(assetId, { signal }),
    enabled: !!assetId,
  });
}

export function useLogicalSegments(assetId: string) {
  return useQuery({
    ...logicalSegmentsQueryOptions(assetId),
    select: (response) => (Array.isArray(response) ? response : response.items),
  });
}

/**
 * Shared options for the cross-scope MCAP segment search (`GET /mcap-segments?q=`), so a route
 * loader can warm the exact query the view reads (mirrors {@link logicalSegmentsQueryOptions}).
 * `query` filters by segment key; empty lists all visible segments. Each hit carries its assetId,
 * so callers don't need to know the owning asset up front.
 */
export function mcapSegmentSearchQuery(
  query: string,
  delivery = 'all',
  deliveryOperator: DeliveryFilterOperator = 'in',
) {
  return infiniteQueryOptions({
    queryKey: [MCAP_QUERY_KEY, 'segment-search', query, delivery, deliveryOperator],
    queryFn: ({ pageParam, signal }) =>
      searchMcapSegments({ q: query, cursor: pageParam, delivery, deliveryOperator }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
}

export interface McapSegmentSearchOptions {
  enabled?: boolean;
  delivery?: string;
  deliveryOperator?: DeliveryFilterOperator;
}

export function useMcapSegmentSearch(
  searchTerm: string,
  { enabled = true, delivery = 'all', deliveryOperator = 'in' }: McapSegmentSearchOptions = {},
) {
  // Keep the prior page visible while a new search term loads, so typing narrows
  // the list in place rather than flashing an empty/loading state (the route's
  // pendingComponent only covers the initial load).
  return useInfiniteQuery({
    ...mcapSegmentSearchQuery(searchTerm, delivery, deliveryOperator),
    enabled,
    placeholderData: keepPreviousData,
    // The dashboard route loader owns its cold failure; dialog and search-key failures stay local.
    throwOnError: false,
  });
}

/**
 * Shared options for a single logical-segment query, so the route loader can prefetch the exact
 * same query the page reads (mirrors {@link logicalSegmentsQueryOptions}). Extracting this lets
 * `$segmentId` own its loading skeleton + error UI at the route level.
 */
export function logicalSegmentQueryOptions(assetId: string, segmentId: string) {
  return queryOptions({
    queryKey: [MCAP_QUERY_KEY, 'segment', assetId, segmentId],
    queryFn: ({ signal }) => fetchLogicalSegment(assetId, segmentId, { signal }),
    enabled: !!assetId && !!segmentId,
  });
}

export function useLogicalSegment(assetId: string, segmentId: string) {
  return useQuery(logicalSegmentQueryOptions(assetId, segmentId));
}

export function useCreateSegmentDelivery(assetId: string, segmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeliveryInput) => createSegmentDelivery(assetId, segmentId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [MCAP_QUERY_KEY, 'deliveries', assetId, segmentId],
      }),
  });
}

/**
 * The segment's durable delivery history as a raw TanStack infinite query.
 *
 * No `throwOnError`: the history sits below the generate-delivery form on the segment page, and
 * escalating would replace the form that is the whole point of the page. The component keeps
 * background polling from dimming the rows on every
 * poll tick would read as a broken list rather than as staleness.
 */
export function useSegmentDeliveries(assetId: string, segmentId: string) {
  return useInfiniteQuery({
    queryKey: [MCAP_QUERY_KEY, 'deliveries', assetId, segmentId],
    queryFn: ({ pageParam, signal }) =>
      fetchSegmentDeliveries(assetId, segmentId, { cursor: pageParam }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!assetId && !!segmentId,
    staleTime: 0,
    refetchInterval: (query) => {
      const deliveries = query.state.data?.pages.flatMap((page) => page.items) ?? [];
      return deliveries.some((delivery) => !isDeliveryTerminal(delivery))
        ? ACTIVE_STATUS_POLL_MS
        : false;
    },
  });
}

export function useSegmentDelivery(assetId: string, segmentId: string, deliveryId: string) {
  return useQuery({
    queryKey: [MCAP_QUERY_KEY, 'delivery', assetId, segmentId, deliveryId],
    queryFn: ({ signal }) => fetchSegmentDelivery(assetId, segmentId, deliveryId, { signal }),
    enabled: !!assetId && !!segmentId && !!deliveryId,
    staleTime: 0,
    refetchInterval: (query) =>
      isDeliveryTerminal(query.state.data) ? false : ACTIVE_STATUS_POLL_MS,
  });
}

export function useDeliveryDownloadLease(
  assetId: string,
  segmentId: string,
  deliveryId: string,
  result: DeliveryResult | null,
  requested: boolean,
) {
  return useQuery({
    queryKey: [MCAP_QUERY_KEY, 'delivery-download', assetId, segmentId, deliveryId],
    queryFn: ({ signal }) => {
      if (!result) throw new Error('delivery_result_not_ready');
      return fetchDeliveryDownloadLease(assetId, segmentId, deliveryId, { signal }).then(
        (lease) => {
          if (
            lease.artifactBlake3 !== result.artifactBlake3 ||
            lease.sizeBytes !== result.artifactSizeBytes
          ) {
            throw new Error('delivery_download_lease_identity_mismatch');
          }
          return lease;
        },
      );
    },
    enabled: requested && !!assetId && !!segmentId && !!deliveryId && !!result,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** The operational segment to persist: an absolute-ns window plus the validation context. */
export interface SubmitSegmentInput {
  logicalStartNs: string;
  logicalEndNs: string;
  videoTracks: ReadonlyArray<VideoTrackCatalogEntry>;
  canonicalRepresentation: McapCanonicalRepresentation;
}

/**
 * Persist the single operational segment (create-only): preview the copy plan, validate it against
 * the asset's video-track catalog and the submitted preview context, then create the logical
 * segment. No delivery is started (that stays a separate action on the segment detail page).
 * Rejects (`SegmentPreviewMismatch` or a transport error) so the caller can keep the draft.
 */
export function useSubmitSegment(assetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubmitSegmentInput): Promise<LogicalSegment> => {
      const previewInput: SegmentPreviewInput = {
        logicalStartNs: input.logicalStartNs,
        logicalEndNs: input.logicalEndNs,
      };
      const plan = await previewSegment(assetId, previewInput);
      const context: SegmentPreviewContext = {
        sourceAssetId: assetId,
        logicalStartNs: input.logicalStartNs,
        logicalEndNs: input.logicalEndNs,
        canonicalRepresentation: input.canonicalRepresentation,
      };
      if (
        !planMatchesVideoTrackCatalog(plan, input.videoTracks) ||
        !planMatchesPreviewContext(plan, context)
      ) {
        throw new SegmentPreviewMismatch();
      }
      return createLogicalSegment(assetId, {
        segmentId: `segment-${crypto.randomUUID()}`,
        expectedPlan: plan,
      });
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [MCAP_QUERY_KEY, 'segments', assetId] }),
        queryClient.invalidateQueries({ queryKey: [MCAP_QUERY_KEY, 'segment-search'] }),
      ]),
  });
}
